import React, { useEffect } from 'react'
import { ModalTemplate, Title } from '@/ui'
import styles from './GroupWorkPlaceModalForm.module.scss'
import { useTranslation } from 'react-i18next'
import { Formik } from 'formik'
import { ValidationSchema } from './validationSchema'
import { GroupWorkPlaceForm } from './GroupWorkPlaceForm'
import { useDispatch, useSelector } from 'react-redux'
import { handbookSelectors } from '@/entity/handbook'
import { LoadingBlocker, Loader } from '@/components/Loader'
import { staffActions, staffSelectors, staffThunks } from '@/entity/staffingProject'
import { GroupWorkPlaceLmcForm } from '@features/staffing/components/groupWorkPlace/GroupWorkPlaceLmcForm'
import { DigitalSign } from '@/utils/digitalSign'
import { message } from 'antd'
import { staffingSelectors, staffingThunks } from '@/entity'
import { toNumber } from '@/utils/number'

function mapDictData(data, position, positionByKnz, laborMobilityCenterCategorys) {
	const isDicPosition2 = data.isDicPosition === '2'

	if (!isDicPosition2 && !position ) {
		message.warning('Добавьте должность в справочник должностей', 10)
		return null
	}

	return {
		destablishedPost: {
			code: isDicPosition2 ? null : position.code,
			nameKz: isDicPosition2 ? data.nameKz : position.kz,
			nameRu: isDicPosition2 ? data.nameRu : position.rus,
		},
		dposition: {
			code: positionByKnz.code,
			nameKz: positionByKnz.kz,
			nameRu: positionByKnz.rus,
		},
		dstaffCategory: {
			code: laborMobilityCenterCategorys.code,
			nameKz: laborMobilityCenterCategorys.kz,
			nameRu: laborMobilityCenterCategorys.rus,
		},
	}
}

export const GroupWorkPlaceModalForm = ({
	initialValues,
	onClose,
	actions,
	activeStructId,
	staff,
	lmc, //Признак ЦТМ (Центра трудовой мобильности)
	groupRow,
	structureDeptTypeCode, // Код типа структуры для автозаполнения категории в школах
}) => {
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const { edit, create } = actions
	const isSuccess = useSelector(staffSelectors.isSuccess)
	const isLoading = useSelector(staffingSelectors.isLoading)
	const positions = useSelector(handbookSelectors.positions)
	const positionsByKnz = useSelector(handbookSelectors.positionsByKnz)
	const laborMobilityCenterCategory = useSelector(handbookSelectors.laborMobilityCenterCategory)
	const isActualStaff = staff.actual

	const {activeCompany} = useSelector((state) => ({
		activeCompany: state.company.company
	}))

	const digitalSign = new DigitalSign()

	const findActiveStruct = (arr, itemId) => (
		arr.reduce((a, item) => {
			if (a) return a;
			if (item.externalId === itemId) return item;
			if (item.childs) return findActiveStruct(item.childs, itemId)
		}, null)
	);

	useEffect(() => {
		let activeStruct = findActiveStruct(staff.childs, activeStructId)

		if (activeCompany?.msu) {
			let checkDeptTypeCode = Number(activeStruct?.deptTypeCode) > 23

			if(checkDeptTypeCode){
				let body = {
					data: activeStruct?.deptTypeCode
				}
				dispatch(staffThunks.getPositionsByDeptType(body))
			} else {
				dispatch(staffActions.getPositionByDeptTypeDone(positionsByKnz))
			}
		} else {
			dispatch(staffActions.getPositionByDeptTypeDone(positionsByKnz))
		}
	}, [])

	useEffect(() => {
		if (isSuccess) {
			onClose()
		}
	}, [isSuccess])

	useEffect(() => {
		if (activeCompany?.sphereType && structureDeptTypeCode) {

			// Проверяем, что код не пустая строка
			if (structureDeptTypeCode.trim() === '') {
				return
			}

			// Формируем data: для healthcare не отправляем code
			const data = {
				sphereType: activeCompany?.sphereType,
			}

			// Добавляем code только если sphereType !== 'healthcare'
			if (activeCompany?.sphereType !== 'healthcare') {
				data.code = structureDeptTypeCode
			}

			const body = {
				data,
				onSuccess: (res) => {},
				onError: () => {}
			}
			dispatch(staffThunks.getEstablishedPostByCategoryCode(body))
		}
	}, [])

	const onSubmit = (data) => {
		let position = positions?.find((item) => item.code === data.destablishedPost)
		let positionByKnz = positionsByKnz?.find((item) => item.code === data.dposition)
		let laborMobilityCenterCategorys = laborMobilityCenterCategory?.find((item) => item.code === data.dstaffCategory)
		const dictMapData = mapDictData(data, position, positionByKnz, laborMobilityCenterCategorys)

		if (!dictMapData) return
		let newGroupWorkPlace = {}
		if (create) {
			newGroupWorkPlace = {
				...dictMapData,
				factSalary: data.factSalary,
				freelance: data.freelance,
				grossSalary: toNumber(data.grossSalary),
				salarySupplement: toNumber(data.salarySupplement),
				structureExternalId: activeStructId,
				workplaceCount: data.workplaceCount,
			}
			dispatch(staffThunks.createGroupWorkPlace(newGroupWorkPlace))
		} else {
			newGroupWorkPlace = {
				...dictMapData,
				freelance: data.freelance,
				posGroupExternalId: groupRow.externalId,
				...(isActualStaff ? { structureExternalId: activeStructId } : {}),
			}
			if (isActualStaff) {
				const type = 'posGroup'
				let body = {
					newGroupWorkPlace,
					type,
					activeStructId,
					onSuccess: async (xml) => {
						const res = await digitalSign.handleSend(xml)

						let signedBody = {
							data: { data: res },

							onSuccess: () => {
								message.success('Группа успешно изменена!')
								onClose()
							},
							type,
							activeStructId,
						}

						dispatch(staffingThunks.sendSignActualGroup(signedBody))
					},
				}

				dispatch(staffingThunks.setActualEditGroup(body))
			} else {
				dispatch(staffThunks.editGroupWorkPlace(newGroupWorkPlace, activeStructId))
			}
		}
	}

	let editGroupWorkPlace
	if (edit && groupRow) {
		let establishedPostNames = { ...groupRow.establishedPost }
		let otherProps = { ...groupRow }
		editGroupWorkPlace = {
			...(establishedPostNames || {}),
			...(otherProps || {}),
			isDicPosition: !establishedPostNames.code ? '2' : '3',
			destablishedPost: establishedPostNames.code ? establishedPostNames.code : null,
			dposition: groupRow?.dposition?.code ? groupRow?.dposition?.code : null,
			dstaffCategory: groupRow?.dstaffCategory?.code ? groupRow?.dstaffCategory?.code : null,
		}
	}


	return (
		<>
			<ModalTemplate onClose={onClose} priority={edit || create ? 2 : 1}>
				<div className={`scroll ${styles.wrapper}`}>
					<Title color="black">{create ? t('staff.create_group') : t('staff.edit_group')}</Title>
					<Formik
						initialValues={create ? initialValues : editGroupWorkPlace}
						validationSchema={ValidationSchema(edit)}
						onSubmit={onSubmit}
					>
						{positions?.length > 0 && positionsByKnz?.length > 0 && laborMobilityCenterCategory?.length > 0 ? (
							<>
								{lmc ? (
									<GroupWorkPlaceLmcForm onClose={onClose} edit={edit} actualStaffing={isActualStaff} />
								) : (
									<GroupWorkPlaceForm 
										onClose={onClose} 
										edit={edit} 
										actualStaffing={isActualStaff}
										structureDeptTypeCode={structureDeptTypeCode}
									/>
								)}
							</>
						) : (
							<div className={styles.loaderWrapper}>
								<Loader />
							</div>
						)}
					</Formik>
				</div>
			</ModalTemplate>

			{isLoading ? <LoadingBlocker /> : null}
		</>
	)
}
